import {
	flip,
	FloatingPortal,
	offset,
	shift,
	useDismiss,
	useFloating,
	useHover,
	useInteractions,
	useTransitionStyles,
} from '@floating-ui/react'
import { type ReactNode, useState } from 'react'

interface UserHoverCardProps {
	user: {
		name?: string | null
		firstname?: string | null
		lastname?: string | null
		email: string | null
		role: string | null
	}
	children: ReactNode
}

function resolveName(user: UserHoverCardProps['user']): string | null {
	if (user.name) { return user.name }
	const parts = [user.firstname, user.lastname].filter(Boolean).join(' ')
	return parts || null
}

function roleLabel(role: string | null): string | null {
	if (!role) { return null }
	return role.charAt(0).toUpperCase() + role.slice(1)
}

export function UserHoverCard({ user, children }: UserHoverCardProps) {
	const [isOpen, setIsOpen] = useState(false)

	const { refs, floatingStyles, context } = useFloating({
		open: isOpen,
		onOpenChange: setIsOpen,
		placement: 'right-start',
		middleware: [offset(8), flip(), shift()],
	})

	const hover = useHover(context, { delay: { open: 400, close: 0 } })
	const dismiss = useDismiss(context)

	const { getReferenceProps, getFloatingProps } = useInteractions([
		hover,
		dismiss,
	])

	const { isMounted, styles } = useTransitionStyles(context, {
		duration: 100,
	})

	const name = resolveName(user)

	return (
		<>
			<div ref={refs.setReference} {...getReferenceProps()}>
				{children}
			</div>
			{isMounted && (
				<FloatingPortal>
					<div ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()}>
						<div
							style={styles}
							className="w-64 bg-white rounded-lg shadow-lg ring-1 ring-grey-200 p-4"
							onClick={(event) => {
								event.preventDefault()
								event.stopPropagation()
							}}
						>
							<div className="mb-3 min-w-0">
								{name && <div className="text-sm font-semibold text-grey-900 truncate">{name}</div>}
								<div className="text-xs text-grey-500 truncate">{user.email}</div>
							</div>
							{user.role && (
								<div className="text-xs text-grey-500">
									<span className="inline-flex items-center rounded-full bg-grey-100 px-2 py-0.5 text-xs font-medium text-grey-600">
										{roleLabel(user.role)}
									</span>
								</div>
							)}
							{user.role === 'admin' && (
								<p className="mt-3 text-xs text-grey-400 italic">
									Admin users cannot have their access revoked
								</p>
							)}
						</div>
					</div>
				</FloatingPortal>
			)}
		</>
	)
}
